import React, { useState } from 'react';
import classNames from 'classnames';
import loadable from '@loadable/component';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { getStartOf, addTime, stringifyDateToISO8601 } from '../../../util/dates';
import {
  MARKETPLACE_TIME_ZONE,
  parseDateContext,
  serializeDateContext,
} from '../../../util/searchDates';
import {
  getISODateString,
  getLocalDateFromISOString,
} from '../../../components/DatePicker/DatePickers/DatePicker.helpers';

import css from './DayOrMonthPicker.module.css';

// Code-split exactly like the stock date pickers are (see components/index.js, which wraps
// every Field*DatePicker in loadable). A static import would drag the calendar and its CSS into
// the SearchPage chunk even with the dateSearch gate off — and the resulting CSS ordering
// clash between the search filters and the calendar is a build warning, not just extra bytes.
const SingleDatePicker = loadable(() =>
  import(
    /* webpackChunkName: "SingleDatePicker" */ '../../../components/DatePicker/DatePickers/SingleDatePicker'
  )
);

const MONTHS_AHEAD = 24;
const DAY_INPUT_ID = 'BookingDateRangeFilter.day';

/**
 * The next MONTHS_AHEAD months, as { month: 'YYYY-MM', label: 'Juni 2027' }.
 *
 * Weddings are booked far ahead, so the list starts at the current month rather than the
 * next one — a couple marrying at the end of this month still needs a caterer.
 *
 * The label is formatted in MARKETPLACE_TIME_ZONE rather than the browser's zone: the Date is
 * a Zurich month start, and a browser west of Zurich would otherwise render it as the previous
 * month.
 */
const upcomingMonths = intl => {
  const first = getStartOf(new Date(), 'month', MARKETPLACE_TIME_ZONE);
  return Array.from({ length: MONTHS_AHEAD }, (_, i) => {
    const date = addTime(first, i, 'months', MARKETPLACE_TIME_ZONE);
    return {
      month: stringifyDateToISO8601(date, MARKETPLACE_TIME_ZONE).slice(0, 7),
      label: intl.formatDate(date, {
        month: 'long',
        year: 'numeric',
        timeZone: MARKETPLACE_TIME_ZONE,
      }),
    };
  });
};

/**
 * Date input for search: one day, one whole month, or nothing at all.
 *
 * A wedding's unit is a single day, so this deliberately is not a check-in/check-out range
 * picker. The "still flexible" escape hatch is load-bearing: venue-first couples pick their
 * date last, and forcing them to invent one is how you lose them.
 *
 * Note on time zones: SingleDatePicker works in plain browser-local Dates, so the day it hands
 * back is converted with the DatePicker's own getISODateString (which reads the Date's local
 * calendar components) rather than with stringifyDateToISO8601 in a fixed zone. Converting a
 * local-midnight Date into Zurich would slide the calendar day by one for anyone browsing from
 * far enough east or west.
 *
 * @component
 * @param {Object} props
 * @param {string|null} props.value current `dates` query param, e.g. '2027-06-14,2027-06-14'
 * @param {string|null} props.dateMode current `dateMode` query param, 'month' or null
 * @param {Function} props.onChange called with { dates, dateMode }, both null to clear
 * @param {string} [props.dayInputId] id for the day input, when another picker is on the page
 * @returns {JSX.Element}
 */
const DayOrMonthPicker = props => {
  const { value, dateMode, onChange, dayInputId = DAY_INPUT_ID } = props;
  const intl = useIntl();

  const initial = parseDateContext(value, dateMode);
  // This picker only has two modes. A 'range' context — which it never writes, but which a stock
  // link or a hand-edited URL can still carry in — opens on the day tab with nothing selected,
  // rather than falling through to the month grid and pretending a span is a month.
  const [mode, setMode] = useState(initial?.mode === 'month' ? 'month' : 'day');

  const selectedMonth = initial?.mode === 'month' ? initial.month : null;
  const selectedDay = initial?.mode === 'day' ? initial.date : null;

  // Today as a calendar day in the marketplace's zone. Blocking is compared as 'YYYY-MM-DD'
  // strings so that the picker refuses exactly the days datesQueryParams would drop.
  const todayISO = stringifyDateToISO8601(
    getStartOf(new Date(), 'day', MARKETPLACE_TIME_ZONE),
    MARKETPLACE_TIME_ZONE
  );

  const isDayBlocked = date =>
    date instanceof Date && !isNaN(date) ? getISODateString(date) < todayISO : false;

  // serializeDateContext returns both halves of the pair, so clearing the day also clears any
  // dateMode that a link or a previous month selection left in the URL.
  const handleDay = date => {
    if (!date) {
      onChange(serializeDateContext(null));
      return;
    }
    onChange(serializeDateContext({ mode: 'day', date: getISODateString(date) }));
  };

  return (
    <div className={css.root}>
      <div className={css.modes}>
        <button
          type="button"
          className={classNames(css.mode, { [css.modeOn]: mode === 'day' })}
          aria-pressed={mode === 'day'}
          onClick={() => setMode('day')}
        >
          <FormattedMessage id="BookingDateRangeFilter.modeDay" />
        </button>
        <button
          type="button"
          className={classNames(css.mode, { [css.modeOn]: mode === 'month' })}
          aria-pressed={mode === 'month'}
          onClick={() => setMode('month')}
        >
          <FormattedMessage id="BookingDateRangeFilter.modeMonth" />
        </button>
      </div>

      {mode === 'day' ? (
        <>
          <label className={css.label} htmlFor={dayInputId}>
            <FormattedMessage id="BookingDateRangeFilter.dayLabel" />
          </label>
          <SingleDatePicker
            id={dayInputId}
            name="searchDate"
            placeholderText={intl.formatMessage({ id: 'BookingDateRangeFilter.dayPlaceholder' })}
            value={selectedDay ? getLocalDateFromISOString(selectedDay) : null}
            isDayBlocked={isDayBlocked}
            onChange={handleDay}
          />
        </>
      ) : (
        <>
          <span className={css.label}>
            <FormattedMessage id="BookingDateRangeFilter.monthLabel" />
          </span>
          <div className={css.monthGrid}>
            {upcomingMonths(intl).map(({ month, label }) => (
              <button
                type="button"
                key={month}
                className={classNames(css.monthCell, {
                  [css.monthCellOn]: month === selectedMonth,
                })}
                aria-pressed={month === selectedMonth}
                onClick={() => onChange(serializeDateContext({ mode: 'month', month }))}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      <button
        type="button"
        className={css.clear}
        onClick={() => onChange(serializeDateContext(null))}
      >
        <FormattedMessage id="BookingDateRangeFilter.clear" />
      </button>
    </div>
  );
};

export default DayOrMonthPicker;
